import { useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { ticketParticipantsSet, ticketUpdate } from '~/actions/ticket';
import { backToReview, getTicketParticipants } from '~/api/tickets';
import { Button } from '~/eds/src/components/Button/Button';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { api } from '~/redux/api/createApi';
import * as TAGS from '~/redux/api/endpoints/allEndpoints';
import Text from '~/ui/components/Text/Text';
import { useModal } from '~/ui/hooks/modal';

import { useActivityLogUpdater } from '../../shared/useActivityLogUpdater';

function BackToReviewAction({
  // connected
  ticketId,
  ticketName,
  ticketUpdate,
  disabled = false,
}) {
  const [shouldResetApprovals, setResetApprovals] = useState(false);
  const updateActivityLog = useActivityLogUpdater({ ticketId });
  const { toast } = useToast();
  const dispatch = useDispatch();
  const intl = useIntl();

  const [modal, showModal] = useModal({
    title: intl.formatMessage({
      id: 'BackToReviewAction.title',
      defaultMessage: 'Return to Review Stage?',
      description: 'Modal title for confirming moving back to review stage.',
    }),
    onHide: () => setResetApprovals(false),
    actionButton: {
      text: intl.formatMessage({
        id: 'BackToReviewAction.actionButtonText',
        defaultMessage: 'Return to Review Stage',
        description: 'Button text to confirm moving back to review.',
      }),
      errorHandler: () =>
        toast({
          message: intl.formatMessage({
            id: 'BackToReviewAction.transitionFailedError',
            defaultMessage:
              'Transition back to Review failed. Please refresh the page and try again.',
            description:
              'Error toast message when transitioning back to review fails.',
          }),
          status: 'danger',
        }),
      promise: async () => {
        const res = await backToReview({ ticketId, shouldResetApprovals });
        ticketUpdate({
          stages: res.stages,
        });
        dispatch(api.util.invalidateTags([TAGS.TAG_BY_TICKET_SUMMARY]));
        const esigErrorMessage = res?.esigErrorMessage;
        esigErrorMessage
          ? toast({
              message: esigErrorMessage,
              status: 'info',
            })
          : toast({
              message: intl.formatMessage(
                {
                  id: 'BackToReviewAction.successMessage',
                  defaultMessage:
                    'Ticket "{ticketName}" successfully returned to Review.',
                  description:
                    'Success toast message when ticket is returned to review. ticketName is the name of the ticket.',
                },
                { ticketName },
              ),
              status: 'success',
            });

        await updateActivityLog();

        const participants = await getTicketParticipants({ ticketId });
        ticketParticipantsSet(participants.data);
      },
    },
    content: (
      <Text color="gray-900" variant="s-spaced">
        {intl.formatMessage({
          id: 'BackToReviewAction.content',
          defaultMessage:
            'Moving the process back to review will invalidate all signatures that have been submitted and retract all that have been sent.',
          description:
            'Content of the modal explaining the consequences of moving back to review.',
        })}
      </Text>
    ),
  });

  return (
    <>
      <Button
        text={intl.formatMessage({
          id: 'BackToReviewAction.buttonText',
          defaultMessage: 'Return to Review Stage',
          description: 'Button text to trigger the back to review action.',
        })}
        onClick={showModal}
        disabled={disabled}
      />
      {modal}
    </>
  );
}

function mapStateToProps({ ticket }) {
  return { ticketId: ticket.id, ticketName: ticket.name };
}

export default connect(mapStateToProps, {
  ticketParticipantsSet,
  ticketUpdate,
})(BackToReviewAction);
